import axios from 'axios';
import { config } from '@/config/environment';
import { parseSlug } from '@/services/tenantDomain';

/** What the neutral entry point needs in order to navigate: a hostname the SERVER resolved. */
export interface TenantDomainLookupResult {
  found: boolean;
  hostname?: string;
  origin?: string;
  /**
   * Present only where D18 is not configured: there are no tenant subdomains to
   * navigate to, so the login still routes by `__tenant` exactly as it does today.
   * Deliberately the same endpoint either way — stock ABP's tenant-by-name oracle is
   * unmounted permanently, and a per-environment fallback would put it straight back.
   */
  tenantId?: string;
}

/**
 * Tenant lookup for the login entry point (M365-Entra-SSO spec, D18).
 *
 * The organization-name lookup resolves `TenantDomain.Slug`, NOT `AbpTenant.Name`, so
 * the subdomain path and the manual fallback answer the same question.
 *
 * **What this file used to be, and why none of it is left.** It wrapped stock ABP's
 * anonymous `/api/abp/multi-tenancy/tenants/by-name/{name}` and `…/by-id/{id}`. That
 * controller is now unmounted server-side, because repointing only the client would
 * have left the enumeration oracle exactly where it was — it stays mounted whatever
 * the frontend calls. Both wrappers therefore pointed at a route that no longer
 * exists, so they are gone rather than kept as dead code, and `resolveTenantDomain`
 * below is the single replacement.
 *
 * Measured against the running host while removing them: the old code read
 * `response.data.isAvailable || true`, and the response field is actually `isActive`.
 * So the expression was `undefined || true` — unconditionally true at both call sites,
 * exactly as the spec suspected, but for a blunter reason than a misplaced `||`.
 */
export class TenantResolverService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.apiBaseUrl;
  }

  /**
   * Resolve an organization name to the login hostname to navigate to.
   *
   * Returns the server's hostname, never a URL built from what the user typed —
   * "take this text and navigate to it" is the shape of an open redirect, so the
   * input stays a lookup key rather than a destination.
   *
   * Active, inactive and unknown all come back the same way; the caller cannot tell
   * which of the three it hit, by design.
   */
  async resolveTenantDomain(slug: string): Promise<TenantDomainLookupResult> {
    // Rejected client-side before the request when it cannot possibly resolve — this
    // is a UX shortcut, not a security control: the server applies the same rules.
    const parsed = parseSlug(slug);
    if (!parsed.ok) {
      return { found: false };
    }

    try {
      const response = await axios.get<TenantDomainLookupResult>(
        `${this.baseUrl}/api/tenant-lookup/resolve`,
        { params: { slug: parsed.value } },
      );

      const data = response.data;
      // "Found" means the server produced something to act on: a hostname to navigate
      // to, or (pre-D18) a tenant id to route by. Anything else is a miss, and a miss
      // looks the same whether the organization is inactive or does not exist.
      return data?.found && (data.hostname || data.tenantId) ? data : { found: false };
    } catch (error) {
      console.warn('Organization lookup failed', error);
      return { found: false };
    }
  }
}

export const tenantResolver = new TenantResolverService();
